import { format, parse, isValid, isAfter, parseISO, formatDistance } from 'date-fns';
import { nl, enUS } from 'date-fns/locale';
// Universal format for backend/storage (YYYY-MM-DD)
export const UNIVERSAL_DATE_FORMAT = 'yyyy-MM-dd';
// Display formats based on locale
export const DISPLAY_FORMATS = {
    nl: 'dd-MM-yyyy', // Dutch format (with dashes)
    en: 'MM/dd/yyyy' // English format (with slashes)
};
// Get the appropriate locale object
export const getLocale = (language) => {
    return language === 'nl' ? nl : enUS;
};
/**
 * Checks if a date string is in the future
 * @param dateString Date string in YYYY-MM-DD format
 * @returns boolean indicating if the date is in the future
 */
export const isFutureDate = (dateString) => {
    if (!dateString)
        return false;
    try {
        // Parse the date string (expecting YYYY-MM-DD format)
        const date = parseISO(dateString);
        if (!isValid(date)) {
            console.warn('[Dates] Invalid date format for future check:', dateString);
            return false;
        }
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Set to beginning of day for accurate comparison
        return isAfter(date, today);
    }
    catch (error) {
        console.error('[Dates] Error checking if date is in future:', error);
        return false;
    }
};
/**
 * Converts a date string from localized format to universal format (YYYY-MM-DD)
 * @param dateString Date string in the user's locale format
 * @param locale User's language preference ('en' or 'nl')
 * @returns Date string in YYYY-MM-DD format
 */
export const toUniversalFormat = (dateString, locale) => {
    if (!dateString)
        return '';
    // If it's already in universal format (YYYY-MM-DD), return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        return dateString;
    }
    // Parse the date using the locale's format
    const parsedDate = parse(dateString, DISPLAY_FORMATS[locale], new Date());
    if (isValid(parsedDate)) {
        return format(parsedDate, UNIVERSAL_DATE_FORMAT);
    }
    console.error('[Dates] Failed to parse date to universal format:', {
        dateString,
        locale,
        format: DISPLAY_FORMATS[locale]
    });
    return dateString; // Return original if parsing fails
};
/**
 * Converts a date string from universal format (YYYY-MM-DD) to localized format
 * @param dateString Date string in YYYY-MM-DD format
 * @param language User's language preference ('en' or 'nl')
 * @returns Date string formatted according to the user's locale
 */
export const toLocalizedFormat = (dateString, language) => {
    if (!dateString)
        return '';
    try {
        // Parse the date (expecting YYYY-MM-DD format)
        const date = parseISO(dateString);
        if (!isValid(date)) {
            console.warn('[Dates] Invalid date format for localization:', dateString);
            return dateString;
        }
        // Format according to the user's locale
        return format(date, DISPLAY_FORMATS[language], { locale: getLocale(language) });
    }
    catch (error) {
        console.error('[Dates] Error formatting date:', error);
        return dateString;
    }
};
/**
 * Formats a date string to a relative time (e.g., "2 days ago")
 * @param dateString Date string (ISO format) or timestamp
 * @param language User's language preference ('en' or 'nl')
 * @returns Relative time string
 */
export const formatRelativeTime = (dateString, language = 'en') => {
    if (!dateString)
        return '';
    try {
        // Parse the date from ISO string or timestamp
        const date = typeof dateString === 'string' ? new Date(dateString) : new Date(dateString);
        // Validate the date
        if (!isValid(date)) {
            console.warn('[Dates] Invalid date for relative formatting:', dateString);
            return '';
        }
        return formatDistance(date, new Date(), {
            addSuffix: true,
            locale: getLocale(language)
        });
    }
    catch (error) {
        console.error('[Dates] Error formatting relative time:', error);
        return '';
    }
};
